// ---------------------------------------------------------------
// useNotifications.js — Campana de notificaciones:
//  · Mensajes del administrador (colección `notifications`) con
//    leído/borrado INDIVIDUAL por usuario (readBy / hiddenBy).
//  · Recordatorios de eventos propios que empiezan en <1 hora
//    (descartables; se recuerda por dispositivo y por día), con
//    notificación del navegador cuando llega la hora.
// ---------------------------------------------------------------
import { useEffect, useMemo, useRef, useState } from "react";
import {
  subscribeNotifications,
  markNotificationRead,
  hideNotification,
} from "../services/notificationService";
import { useDayEvents } from "./useEvents";
import { useSwaps } from "./useSwaps";
import { useSchedule } from "./useSchedule";
import { monthKey } from "../services/scheduleService";
import {
  subscribeTasks,
  subscribeTaskDone,
  tasksDueFor,
} from "../services/taskService";
import {
  subscribeUserDay,
  getConsentOnce,
  subscribeMisSolicitudes,
  subscribeSolicitudesPendientes,
} from "../services/timeclockService";
import { subscribeNotasTurno } from "../services/shiftNoteService";
import { toISO, timeToMinutes } from "../utils/date";

// Márgenes del recordatorio de fichaje (minutos)
const IN_GRACE = 10; // avisa si ya pasaron 10 min de tu hora de entrada
const OUT_GRACE = 2; // avisa poco después de tu hora de salida
const PENDING_BEFORE_MIN = 45; // aviso de tareas pendientes antes de cerrar

const DISMISSED_KEY = "notif-dismissed";

const loadDismissed = () => {
  try {
    return new Set(JSON.parse(localStorage.getItem(DISMISSED_KEY) || "[]"));
  } catch {
    return new Set();
  }
};

export function useNotifications(user, isAdmin) {
  const [messages, setMessages] = useState([]);
  const [now, setNow] = useState(() => new Date());
  const [dismissed, setDismissed] = useState(loadDismissed);
  const notifiedRef = useRef(new Set());

  const swaps = useSwaps(user?.uid, isAdmin);

  // Mensajes del admin en tiempo real
  useEffect(() => {
    if (!user) return undefined;
    const unsubscribe = subscribeNotifications(
      user.uid,
      setMessages,
      (error) => console.error("Error leyendo notificaciones:", error)
    );
    return unsubscribe;
  }, [user]);

  // Tic de reloj para los recordatorios
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(id);
  }, []);

  const todayISO = toISO(now);
  // Sólo los eventos de HOY: es lo único que necesita la campana
  const events = useDayEvents(user, todayISO);

  // ---- Tareas de turno que tocan HOY (avisan a quien las tiene) ----
  const [tasks, setTasks] = useState([]);
  const [taskDoneMap, setTaskDoneMap] = useState({});
  const { schedule: mySchedule } = useSchedule(
    user?.uid,
    monthKey(now.getFullYear(), now.getMonth() + 1)
  );

  useEffect(() => {
    if (!user) return undefined;
    const unsubscribe = subscribeTasks(
      setTasks,
      (error) => console.error("Error leyendo tareas:", error),
      { uid: user.uid, isAdmin }
    );
    return unsubscribe;
  }, [user, isAdmin]);

  useEffect(() => {
    if (!user) return undefined;
    const unsubscribe = subscribeTaskDone(todayISO, setTaskDoneMap, (error) =>
      console.error("Error leyendo tareas completadas:", error)
    );
    return unsubscribe;
  }, [user, todayISO]);

  const taskItems = useMemo(() => {
    if (!user) return [];
    return tasksDueFor(tasks, user.uid, todayISO, mySchedule?.days)
      .filter((t) => !taskDoneMap[t.id]) // las hechas ya no avisan
      .map((t) => ({
        id: `task-${t.id}-${todayISO}`,
        type: "task",
        title: `Tarea de hoy: ${t.title}`,
        body:
          t.assignedTo === "all"
            ? "Para todo el equipo · toca para verla"
            : "Asignada a ti · toca para verla",
        // (isAssignedTo ya filtró en tasksDueFor; aquí sólo el texto)
        link: "tasks",
        unread: true,
      }))
      .filter((t) => !dismissed.has(t.id));
  }, [tasks, taskDoneMap, user, todayISO, mySchedule, dismissed]);

  // ---- Aviso de cierre: tareas de hoy sin terminar ----
  // Se avisa al final de TU turno (45 min antes de salir). El aviso
  // llega sólo a quien tiene la tarea: si es de una persona, a ella;
  // si es del equipo, a todo el mundo (lo resuelve tasksDueFor).
  const pendingItems = useMemo(() => {
    if (!user) return [];
    const info = mySchedule?.days?.[String(now.getDate())];
    const nowMin = now.getHours() * 60 + now.getMinutes();

    let inWindow = false;
    if (info?.shifts?.length && !info.baja && !info.ownVacation) {
      const ends = info.shifts.map((s) => {
        const a = timeToMinutes(s.start);
        let b = timeToMinutes(s.end);
        if (b <= a) b = 24 * 60; // guardia nocturna
        return b;
      });
      const last = Math.max(...ends);
      inWindow = nowMin >= last - PENDING_BEFORE_MIN && nowMin < last + 60;
    } else if (isAdmin) {
      // El admin, que puede no tener turno, lo ve al cierre
      inWindow = nowMin >= 20 * 60;
    }
    if (!inWindow) return [];

    const pending = tasksDueFor(tasks, user.uid, todayISO, mySchedule?.days)
      .filter((t) => !taskDoneMap[t.id]);
    if (pending.length === 0) return [];

    const id = `taskpending-${todayISO}`;
    if (dismissed.has(id)) return [];
    return [
      {
        id,
        type: "task",
        title: `Quedan ${pending.length} ${
          pending.length === 1 ? "tarea" : "tareas"
        } por completar hoy`,
        body:
          pending.slice(0, 3).map((t) => t.title).join(" · ") +
          (pending.length > 3 ? "…" : ""),
        link: "tasks",
        unread: true,
      },
    ];
  }, [tasks, taskDoneMap, user, isAdmin, todayISO, mySchedule, now, dismissed]);

  // ---- Recordatorio de fichaje (sólo si aceptó el fichaje) ----
  const [clockDays, setClockDays] = useState({});
  const [consentOk, setConsentOk] = useState(false);

  // El consentimiento se lee UNA vez (no cambia sobre la marcha)
  useEffect(() => {
    if (!user) return;
    let alive = true;
    getConsentOnce(user.uid)
      .then((d) => alive && setConsentOk(!!d?.accepted))
      .catch(() => alive && setConsentOk(false));
    return () => {
      alive = false;
    };
  }, [user]);

  useEffect(() => {
    if (!user) return undefined;
    return subscribeUserDay(user.uid, todayISO, setClockDays, (e) =>
      console.error("Error leyendo fichajes:", e)
    );
  }, [user, todayISO]);

  const clockItems = useMemo(() => {
    if (!user || !consentOk) return [];
    const info = mySchedule?.days?.[String(now.getDate())];
    if (
      !info ||
      info.baja ||
      info.ownVacation ||
      info.guardRest ||
      !info.shifts?.length
    ) {
      return [];
    }
    const nowMin = now.getHours() * 60 + now.getMinutes();
    const shifts = [...info.shifts].sort(
      (a, b) => timeToMinutes(a.start) - timeToMinutes(b.start)
    );
    const entries = [...(clockDays[todayISO]?.entries || [])]
      .filter((e) => e.in)
      .sort((a, b) => timeToMinutes(a.in) - timeToMinutes(b.in));

    const out = [];
    for (let i = 0; i < shifts.length; i++) {
      const s = shifts[i];
      const start = timeToMinutes(s.start);
      let end = timeToMinutes(s.end);
      if (end <= start) end = 24 * 60; // guardia nocturna
      const entry = entries[i];

      // ¿Se le olvidó fichar la ENTRADA? (mientras dura el turno)
      if (!entry && nowMin >= start + IN_GRACE && nowMin < end) {
        out.push({
          id: `clockin-${todayISO}-${s.start}`,
          type: "clock",
          title: "No has fichado la entrada",
          body: `Tu turno empezó a las ${s.start} · toca para fichar`,
          link: "timeclock",
          unread: true,
        });
      }

      // ¿Jornada abierta pasada la hora de salida?
      if (entry && !entry.out && nowMin >= end + OUT_GRACE) {
        out.push({
          id: `clockout-${todayISO}-${s.start}`,
          type: "clock",
          title: "Tienes el fichaje abierto",
          body: `Tu turno acabó a las ${s.end} · recuerda fichar la salida`,
          link: "timeclock",
          unread: true,
        });
      }
    }
    return out.filter((r) => !dismissed.has(r.id));
  }, [user, consentOk, mySchedule, clockDays, todayISO, now, dismissed]);

  // Recordatorios: eventos de HOY que empiezan en la próxima hora
  const reminders = useMemo(() => {
    const nowMin = now.getHours() * 60 + now.getMinutes();
    return events
      .filter((ev) => ev.date === todayISO && ev.startTime)
      .map((ev) => ({ ev, start: timeToMinutes(ev.startTime) }))
      .filter(({ start }) => start >= nowMin - 5 && start <= nowMin + 60)
      .map(({ ev, start }) => ({
        id: `reminder-${ev.id}-${todayISO}`,
        type: "reminder",
        title:
          start <= nowMin
            ? `"${ev.title}" está empezando`
            : `"${ev.title}" empieza a las ${ev.startTime}`,
        body: ev.location || "",
        at: null,
        unread: true,
      }))
      .filter((r) => !dismissed.has(r.id))
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [events, now, todayISO, dismissed]);

  // Notificación del navegador justo cuando llega la hora del evento
  useEffect(() => {
    if (typeof Notification === "undefined") return;
    if (Notification.permission !== "granted") return;
    const nowMin = now.getHours() * 60 + now.getMinutes();
    for (const ev of events) {
      if (ev.date !== todayISO || !ev.startTime) continue;
      const start = timeToMinutes(ev.startTime);
      if (start <= nowMin && start > nowMin - 2 && !notifiedRef.current.has(ev.id)) {
        notifiedRef.current.add(ev.id);
        try {
          new Notification("Farmacia Buendía", {
            body: `${ev.title} · ${ev.startTime}`,
          });
        } catch (e) {
          /* sin soporte, ignorar */
        }
      }
    }
  }, [events, now, todayISO]);

  const fmtSwapDate = (iso) =>
    new Date(iso + "T12:00").toLocaleDateString("es-ES", {
      day: "numeric",
      month: "short",
    });

  const swapItems = useMemo(() => {
    const uid = user?.uid;
    const out = [];
    for (const r of swaps) {
      const dates = `${fmtSwapDate(r.fromDate)} ⇄ ${fmtSwapDate(r.toDate)}`;
      if (r.status === "pending_peer" && r.toUid === uid) {
        out.push({
          id: `swap-${r.id}-peer`,
          type: "swap",
          dismissible: false,
          title: `@${r.fromName} te propone un intercambio`,
          body: `Su ${fmtSwapDate(r.fromDate)} por tu ${fmtSwapDate(r.toDate)} · toca para responder`,
          link: "schedule",
          unread: true,
        });
      } else if (r.status === "pending_admin" && isAdmin) {
        out.push({
          id: `swap-${r.id}-admin`,
          type: "swap",
          dismissible: false,
          title: `Intercambio pendiente de aprobar`,
          body: `@${r.fromName} ⇄ @${r.toName} (${dates}) · toca para resolver`,
          link: "schedule",
          unread: true,
        });
      } else if (
        ["approved", "rejected_peer", "rejected_admin"].includes(r.status) &&
        (r.fromUid === uid || r.toUid === uid)
      ) {
        const id = `swap-${r.id}-${r.status}`;
        if (dismissed.has(id)) continue;
        out.push({
          id,
          type: "swap",
          dismissible: true,
          title:
            r.status === "approved"
              ? "Intercambio aprobado"
              : r.status === "rejected_peer"
              ? `@${r.toName} rechazó el intercambio`
              : "Intercambio rechazado por administración",
          body: `@${r.fromName} ⇄ @${r.toName} (${dates})${
            r.status === "approved" ? " · horarios actualizados" : ""
          }`,
          link: "schedule",
          unread: true,
        });
      }
    }
    return out;
  }, [swaps, user, isAdmin, dismissed]);

  /* ---- Notas del equipo ----
     La función se hizo para que un turno le deje un aviso al
     siguiente, pero la nota sólo la veía quien entrase en el
     dashboard y se fijara. Sin aviso, una nota no cumple su único
     cometido. Aquí entra en la campana como una más.

     No avisan las tuyas propias (ya sabes lo que escribiste) ni las
     caducadas: la consulta ya las deja fuera. */
  const [notasTurno, setNotasTurno] = useState([]);
  useEffect(
    () =>
      subscribeNotasTurno(setNotasTurno, () => setNotasTurno([])),
    []
  );

  const noteItems = useMemo(() => {
    const uid = user?.uid;
    if (!uid) return [];
    return notasTurno
      .filter((n) => n.authorUid !== uid)
      .map((n) => ({
        id: `nota-${n.id}`,
        type: "note",
        title: `${n.authorName || "Alguien"} ha dejado una nota`,
        // El texto entero: una nota de turno es corta por definición
        // (500 caracteres como mucho) y truncarla obligaría a ir a
        // buscarla justo cuando lo que se quiere es leerla.
        body: n.text,
        at: n.createdAt?.seconds ? new Date(n.createdAt.seconds * 1000) : null,
        link: "dashboard",
        dismissible: true,
        unread: true,
      }))
      .filter((n) => !dismissed.has(n.id));
  }, [notasTurno, user, dismissed]);

  /* ---- Correcciones de fichaje ----
     A la dirección: cada petición pendiente (no se descarta: se va
     sola al contestarla). A la persona: la respuesta, aprobada o
     rechazada, durante dos semanas o hasta que la lea. */
  const [solicitudes, setSolicitudes] = useState([]);
  useEffect(() => {
    if (!user) return undefined;
    return isAdmin
      ? subscribeSolicitudesPendientes(setSolicitudes, () => setSolicitudes([]))
      : subscribeMisSolicitudes(user.uid, setSolicitudes, () => setSolicitudes([]));
  }, [user, isAdmin]);

  const solicitudItems = useMemo(() => {
    const fecha = (iso) =>
      new Date(iso + "T12:00").toLocaleDateString("es-ES", {
        weekday: "short",
        day: "numeric",
        month: "short",
      });
    if (isAdmin) {
      return solicitudes.map((x) => ({
        id: `solicitud-${x.id}-pendiente`,
        type: "clock",
        dismissible: false,
        title: `@${x.userName || "?"} pide corregir su fichaje`,
        body: `${fecha(x.date)} · toca para revisarlo`,
        at: x.creadaAt?.seconds ? new Date(x.creadaAt.seconds * 1000) : null,
        link: "timeclock",
        unread: true,
      }));
    }
    const hace14 = Date.now() / 1000 - 14 * 86400;
    return solicitudes
      .filter(
        (x) =>
          ["aprobada", "rechazada"].includes(x.estado) &&
          (x.resueltaAt?.seconds || 0) > hace14
      )
      .map((x) => ({
        id: `solicitud-${x.id}-${x.estado}`,
        type: "clock",
        dismissible: true,
        title:
          x.estado === "aprobada"
            ? `Corrección del ${fecha(x.date)} aprobada`
            : `Corrección del ${fecha(x.date)} rechazada`,
        body: x.respuesta || (x.estado === "aprobada" ? "Tu fichaje ya está corregido" : ""),
        at: x.resueltaAt?.seconds ? new Date(x.resueltaAt.seconds * 1000) : null,
        link: "timeclock",
        unread: true,
      }))
      .filter((x) => !dismissed.has(x.id));
  }, [solicitudes, isAdmin, dismissed]);

  const items = useMemo(() => {
    const uid = user?.uid;
    const msgs = messages
      .filter((m) => !m.hiddenBy?.[uid])
      .map((m) => ({
        id: m.id,
        type: "message",
        title: m.title,
        body: m.body,
        from: m.fromName,
        at: m.createdAt?.seconds ? new Date(m.createdAt.seconds * 1000) : null,
        unread: !m.readBy?.[uid],
        // Los avisos de horario llevan a Horario al pulsarlos
        link: m.link,
      }));
    // En la ventana de cierre se muestra el resumen en lugar de un
    // aviso por cada tarea (así no se llena la campana)
    const tasksBlock = pendingItems.length > 0 ? pendingItems : taskItems;
    return [
      ...solicitudItems,
      ...clockItems,
      ...swapItems,
      ...noteItems,
      ...tasksBlock,
      ...reminders,
      ...msgs,
    ];
  }, [
    messages,
    reminders,
    swapItems,
    noteItems,
    taskItems,
    pendingItems,
    clockItems,
    solicitudItems,
    user,
  ]);

  const unread = items.filter((i) => i.unread).length;

  /** Marca UNA notificación como leída */
  const markRead = (item) => {
    if (item.type === "message") {
      markNotificationRead(item.id, user.uid).catch(console.error);
    } else if (item.dismissible !== false) {
      dismissReminderId(item.id); // leído = descartado
    }
  };

  /** Marca como leídas TODAS las que hay ahora en la campana */
  const markAllRead = () => {
    const ids = [];
    for (const it of items) {
      if (!it.unread) continue;
      if (it.type === "message") {
        markNotificationRead(it.id, user.uid).catch(console.error);
      } else if (it.dismissible !== false) {
        ids.push(it.id);
      }
    }
    if (ids.length) dismissReminderIds(ids);
  };

  /** Borra UNA notificación (sólo para este usuario) */
  const removeItem = (item) => {
    if (item.type === "message") {
      hideNotification(item.id, user.uid).catch(console.error);
    } else if (item.dismissible !== false) {
      dismissReminderId(item.id);
    }
  };

  const dismissReminderId = (id) => dismissReminderIds([id]);

  const dismissReminderIds = (ids) => {
    setDismissed((prev) => {
      const next = new Set(prev);
      for (const id of ids) next.add(id);
      // Conservar sólo los descartes de hoy para no crecer sin fin.
      // Ojo: los avisos de fichaje son "clockin-2026-08-05-09:00", con
      // la hora del turno al final, así que hay que buscar la fecha
      // DENTRO del id. Con endsWith se borraba el descarte nada más
      // hacerlo y el aviso volvía a salir una y otra vez.
      const pruned = [...next].filter(
        (x) =>
          x.includes(todayISO) || x.startsWith("swap-") || x.startsWith("solicitud-")
      );
      localStorage.setItem(DISMISSED_KEY, JSON.stringify(pruned));
      return new Set(pruned);
    });
  };

  /** Pide permiso de notificaciones del navegador (al abrir la campana) */
  const requestPermission = () => {
    if (typeof Notification !== "undefined" && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
  };

  return { items, unread, markRead, markAllRead, removeItem, requestPermission };
}
